import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../components/home/Hero';
import Services from '../components/home/Services';
import Gallery from '../components/home/Gallery';
import ContactForm from '../components/home/ContactForm';
import styled from 'styled-components';
import { FaTruck, FaRecycle, FaCalendarCheck, FaAward } from 'react-icons/fa';

const Home = () => {
  return (
    <>
      <Helmet>
        <title>Florida's Top Site Service & Demolition | Professional Demolition Experts</title>
        <meta name="description" content="Professional site clearing and demolition services in Crescent City, Florida. Service-based business – we come to you! Specializing in residential and commercial demolition, concrete removal, and site preparation." />
        <meta name="keywords" content="site clearing, demolition services, concrete removal, tree removal, property clearing, construction site preparation, demolition contractor, mobile service, on-site service, Crescent City, Palatka, Putnam County" />
        <link rel="canonical" href="https://www.floridastopsite.com/" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Florida's Top Site Service & Demolition | Professional Demolition Experts" />
        <meta property="og:description" content="Professional site clearing and demolition services in Crescent City, Florida. Service-based business – we come to you! Specializing in residential and commercial demolition, concrete removal, and site preparation." />
        <meta property="og:url" content="https://www.floridastopsite.com/" />
        <meta property="og:image" content="https://www.floridastopsite.com/images/og-image.jpg" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Florida's Top Site Service & Demolition",
            "image": "https://www.floridastopsite.com/images/logo.jpg",
            "url": "https://www.floridastopsite.com",
            "telephone": ["+13868722144", "+15127674123"],
            "email": "FLSITESERVICE@GMAIL.COM",
            "priceRange": "$$",
            "description": "Service-based business – we come to you! Based in Crescent City, FL",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "123 Demolition Ave",
              "addressLocality": "Crescent City",
              "addressRegion": "FL",
              "postalCode": "32112",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 29.430839,
              "longitude": -81.508171
            },
            "areaServed": {
              "@type": "City",
              "name": "Crescent City, FL and surrounding areas"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday"
              ],
              "opens": "00:00",
              "closes": "24:00"
            },
            "sameAs": [
              "https://www.facebook.com/floridastopsite",
              "https://www.instagram.com/floridastopsite",
              "https://twitter.com/floridastopsite"
            ]
          })}
        </script>
        
        {/* Service Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Demolition and Site Clearing",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Florida's Top Site Service & Demolition",
              "description": "Service-based business – we come to you!"
            },
            "areaServed": [
              {
                "@type": "City",
                "name": "Crescent City"
              },
              {
                "@type": "State",
                "name": "Florida"
              }
            ],
            "description": "Professional demolition and site clearing services for residential and commercial properties. We offer complete demolition, concrete removal, site preparation, and tree removal. Service-based business – we come to you!",
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "Contact for quote",
                "priceCurrency": "USD"
              }
            }
          })}
        </script>
      </Helmet>
      
      <Hero />
      
      <Features>
        <div className="container">
          <FeaturesGrid>
            <FeatureItem className="animate-on-scroll slide-up delay-100">
              <FeatureIcon>
                <FaTruck />
              </FeatureIcon>
              <FeatureTitle>Modern Equipment</FeatureTitle>
              <FeatureDescription>
                We utilize the latest equipment and technology for efficient and safe demolition.
              </FeatureDescription>
            </FeatureItem>
            
            <FeatureItem className="animate-on-scroll slide-up delay-200">
              <FeatureIcon>
                <FaRecycle />
              </FeatureIcon>
              <FeatureTitle>Eco-Friendly Practices</FeatureTitle>
              <FeatureDescription>
                We recycle and properly dispose of materials to minimize environmental impact.
              </FeatureDescription>
            </FeatureItem>
            
            <FeatureItem className="animate-on-scroll slide-up delay-300">
              <FeatureIcon>
                <FaCalendarCheck />
              </FeatureIcon>
              <FeatureTitle>On-Time Completion</FeatureTitle>
              <FeatureDescription>
                We understand the importance of timelines and complete projects on schedule.
              </FeatureDescription>
            </FeatureItem>
            
            <FeatureItem className="animate-on-scroll slide-up delay-400">
              <FeatureIcon>
                <FaAward />
              </FeatureIcon>
              <FeatureTitle>Licensed & Insured</FeatureTitle>
              <FeatureDescription>
                Fully licensed, bonded, and insured for your complete peace of mind.
              </FeatureDescription>
            </FeatureItem>
          </FeaturesGrid>
        </div>
      </Features>
      
      <Services />
      <Gallery />
      
      <Testimonials>
        <div className="container">
          <div className="section-title animate-on-scroll slide-down">
            <h2>Client Testimonials</h2>
            <p>What our satisfied customers say about our services</p>
          </div>
          
          <TestimonialsGrid>
            <TestimonialItem className="animate-on-scroll slide-up delay-100">
              <TestimonialQuote>
                "Florida's Top Demolition Services did an excellent job on our commercial property demolition. They were professional, efficient, and completed the project ahead of schedule. Highly recommended!"
              </TestimonialQuote>
              <TestimonialAuthor>
                <strong>Michael Johnson</strong>
                <span>Johnson Development</span>
              </TestimonialAuthor>
            </TestimonialItem>
            
            <TestimonialItem className="animate-on-scroll slide-up delay-200">
              <TestimonialQuote>
                "We hired Florida's Top Demolition Services for our residential site clearing project, and they exceeded our expectations. Their attention to detail and commitment to safety was impressive. Great company to work with!"
              </TestimonialQuote>
              <TestimonialAuthor>
                <strong>Sarah Williams</strong>
                <span>Homeowner, Crescent City</span>
              </TestimonialAuthor>
            </TestimonialItem>
            
            <TestimonialItem className="animate-on-scroll slide-up delay-300">
              <TestimonialQuote>
                "The team at Florida's Top Demolition Services is incredibly knowledgeable and professional. They helped us navigate the permit process and completed our demolition project with precision and care."
              </TestimonialQuote>
              <TestimonialAuthor>
                <strong>David Martinez</strong>
                <span>Martinez Construction, Palatka</span>
              </TestimonialAuthor>
            </TestimonialItem>
          </TestimonialsGrid>
        </div>
      </Testimonials>
      
      <ContactForm />
    </>
  );
};

const Features = styled.section`
  padding: 8rem 0;
  background-color: var(--background-color);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.9)), url('/images/services/residential-demo.jpg');
    background-size: cover;
    background-position: center;
    opacity: 0.2;
    z-index: 0;
  }
  
  & > .container {
    position: relative;
    z-index: 1;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  border-radius: 10px;
  background: linear-gradient(145deg, #1a1a1a, #2a2a2a);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 140, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 30px rgba(255, 140, 0, 0.3);
    border-color: rgba(255, 140, 0, 0.3);
  }
`;

const FeatureIcon = styled.div`
  font-size: 5rem;
  color: var(--primary-color);
  margin-bottom: 2rem;
`;

const FeatureTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
`;

const FeatureDescription = styled.p`
  font-size: 1.6rem;
  color: var(--text-color);
`;

const Testimonials = styled.section`
  padding: 8rem 0;
  background-color: var(--secondary-color);
  
  .section-title {
    h2, p {
      color: white;
    }
    
    h2::after {
      background-color: var(--primary-color);
    }
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  margin-top: 5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialItem = styled.div`
  background-color: var(--background-color);
  padding: 3rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 2rem;
    left: 2rem;
    font-size: 8rem;
    color: var(--medium-gray);
    font-family: Georgia, serif;
    line-height: 1;
    z-index: 0;
  }
`;

const TestimonialQuote = styled.p`
  font-size: 1.6rem;
  line-height: 1.8;
  color: var(--text-color);
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  flex-direction: column;
  
  strong {
    font-size: 1.8rem;
    color: var(--primary-color);
  }
  
  span {
    font-size: 1.4rem;
    color: white;
  }
`;

export default Home; 